import { getSignedUrlForGet } from '@commenda/api';
import { Alert, Skeleton } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { renderAsync } from 'docx-preview';
import { useEffect, useRef, useState } from 'react';

/** Word's own styles are rendered into our DOM, so the document keeps its fonts, colours and images while
 *  staying queryable. Headers and footers are left off: they repeat on every page and add noise to a pane
 *  whose job is the body text. */
const RENDER_OPTIONS = {
  inWrapper: true,
  breakPages: true,
  ignoreWidth: false,
  ignoreHeight: true,
  ignoreFonts: false,
  renderHeaders: false,
  renderFooters: false,
  renderFootnotes: true,
  renderEndnotes: true,
  useBase64URL: true,
  className: 'docx',
};

type TPaneState = 'loading' | 'ready' | 'unreachable' | 'unreadable';

interface IDocumentAssemblerDocumentPaneProps {
  /** The stored Word file to show. Null while the version it belongs to is still loading. */
  fileId: number | null;
}

/**
 * The template rendered from its own .docx, in our own DOM.
 *
 * Nothing is converted to a simpler markup on the way, so fonts, colours, numbering, tables and images
 * survive, and the client's file is not handed to a third-party viewer to be rendered.
 */
export const DocumentAssemblerDocumentPane = ({
  fileId,
}: IDocumentAssemblerDocumentPaneProps) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const styleRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<TPaneState>('loading');

  useEffect(() => {
    let isCurrent = true;
    setState('loading');
    bodyRef.current?.replaceChildren();

    if (fileId === null) return undefined;

    const render = async () => {
      const signed = await getSignedUrlForGet(fileId);
      if (!isCurrent) return;

      if (!signed?.signedUrl) {
        setState('unreachable');

        return;
      }

      // A cross-origin read, so it depends on the bucket allowing GET from this origin.
      const response = await fetch(signed.signedUrl);
      if (!isCurrent) return;

      if (!response.ok) {
        setState('unreachable');

        return;
      }

      const blob = await response.blob();
      if (!isCurrent || !bodyRef.current) return;

      // The container is emptied first: re-rendering appends, so switching versions would stack documents.
      bodyRef.current.replaceChildren();

      await renderAsync(
        blob,
        bodyRef.current,
        styleRef.current ?? undefined,
        RENDER_OPTIONS
      );
      if (!isCurrent) return;

      setState('ready');
    };

    render().catch(() => {
      if (isCurrent) setState('unreadable');
    });

    return () => {
      isCurrent = false;
    };
  }, [fileId]);

  return (
    <div
      aria-busy={state === 'loading'}
      className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-bg-secondary"
    >
      {state === 'unreachable' || state === 'unreadable' ? (
        <div className="p-3xl">
          <Alert
            variant="destructive"
            title={
              state === 'unreachable'
                ? 'This template could not be opened'
                : 'This template could not be shown'
            }
            description={
              state === 'unreachable'
                ? "Its stored file could not be reached. Download it from the version's file card instead."
                : 'The file is stored, but it could not be read as a Word document. Download it to open it in Word.'
            }
          />
        </div>
      ) : null}

      {state === 'loading' ? (
        <div className="flex flex-col gap-lg p-3xl">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      ) : null}

      <div ref={styleRef} className="hidden" />
      <div
        ref={bodyRef}
        role="document"
        className={cn(
          'flex justify-center py-3xl',
          state !== 'ready' && 'hidden',
          // Only the library's backdrop is replaced. Its page itself — white, shadowed, spaced — is left
          // alone: that one rule carries the background as well as the shadow.
          '[&_.docx-wrapper]:bg-transparent [&_.docx-wrapper]:p-none'
        )}
      />
    </div>
  );
};
